import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import classes from '../../../pipeline.module.scss';
import Utils from '../../../../../../../utils/utils';

export const chartColors = [
  '#547EEE',
  '#E905E0',
  '#27AE60',
  '#56CCF2',
  '#F2C94C',
  '#EB5757',
  '#6FCF97',
  '#9B51E0',
  '#4F5D75',
  '#F2994A',
];

const shortCount = (value) => (value ? Utils.formattingCount(value) : '');

const PivotsChart = ({
  categories, series, height = 300, stacked = false, distributed = false, showLegend, colors = chartColors,
}) => {
  const [hidden, setHidden] = useState([]);

  const wrapperRef = useRef(null);

  // legend is on by default only where it means something - several series in one chart
  const legendVisible = showLegend === undefined ? (!distributed && series.length > 1) : showLegend;

  // in a distributed chart the legend items are categories, not series, and apexcharts
  // cannot toggle those - so that legend is ours and hiding drops the column from the data
  const customLegend = distributed && legendVisible;

  const categoriesKey = categories.join('|');

  // a new dataset brings new categories - nothing stays hidden from the previous one
  useEffect(() => {
    setHidden((prev) => (prev.length ? [] : prev));
  }, [categoriesKey]);

  const { shownCategories, shownSeries, barColors } = useMemo(() => {
    const kept = categories
      .map((category, index) => index)
      .filter((index) => !customLegend || !hidden.includes(categories[index]));

    return {
      shownCategories: kept.map((index) => categories[index]),
      shownSeries: series.map((item) => ({
        ...item,
        data: kept.map((index) => item.data[index] || 0),
      })),
      // distributed colours go by data point, so they have to follow the kept categories
      barColors: distributed ? kept.map((index) => colors[index % colors.length]) : colors,
    };
  }, [categories, series, hidden, customLegend, distributed, colors]);

  const toggleCategory = (category) => setHidden((prev) => (prev.includes(category)
    ? prev.filter((item) => item !== category)
    : [...prev, category]));

  /**
   * Apexcharts fades the other series when a legend item is hovered, but only for real
   * series - in a distributed chart every column is one data point of a single series.
   * The same effect is done here by hand, on the rendered nodes, so hovering never has
   * to re-render the chart.
   */
  const highlightPoint = useCallback((point) => {
    const root = wrapperRef.current;

    if (!root) return;

    // a hidden category has no column left to highlight
    const target = point === null || point < 0 ? null : point;

    const fade = (nodes) => nodes.forEach((node, index) => {
      const own = node.getAttribute('j');
      const pointIndex = own === null ? index : Number(own);

      node.style.opacity = target === null || target === pointIndex ? '' : '0.25';
    });

    fade([...root.querySelectorAll('.apexcharts-bar-area')]);
    fade([...root.querySelectorAll('.apexcharts-datalabel')]);
  }, []);

  const options = useMemo(() => ({
    chart: {
      type: 'bar',
      stacked,
      toolbar: { show: false },
      fontFamily: 'inherit',
      zoom: { enabled: false },
      animations: { enabled: false },
      events: customLegend
        ? {
          dataPointMouseEnter: (event, ctx, config) => highlightPoint(config.dataPointIndex),
          dataPointMouseLeave: () => highlightPoint(null),
          mouseLeave: () => highlightPoint(null),
        }
        : {},
    },

    colors: barColors,

    plotOptions: {
      bar: {
        distributed,
        columnWidth: shownSeries.length > 1 ? '75%' : '45%',
        borderRadius: 3,
        dataLabels: { position: 'top' },
      },
    },

    dataLabels: {
      enabled: true,
      offsetY: stacked ? 0 : -17,
      formatter: (value) => shortCount(value),
      style: {
        fontSize: '10px',
        fontWeight: 500,
        colors: stacked ? ['#FFFFFF'] : ['#5F6C8E'],
      },
    },

    stroke: { show: false },

    xaxis: {
      categories: shownCategories,
      labels: {
        style: { colors: '#8A94A6', fontSize: '11px' },
        trim: true,
        hideOverlappingLabels: false,
        rotateAlways: false,
        rotate: -25,
      },
      axisBorder: { color: '#ECEEF2' },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },

    yaxis: {
      labels: {
        style: { colors: '#8A94A6', fontSize: '11px' },
        formatter: (value) => shortCount(Math.round(value)),
      },
    },

    grid: {
      borderColor: '#F0F2F7',
      strokeDashArray: 4,
      padding: { top: 10, right: 6, left: 6 },
      xaxis: { lines: { show: false } },
    },

    legend: {
      show: legendVisible && !customLegend,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      offsetY: 3,
      itemMargin: { horizontal: 8, vertical: 3 },
      markers: { width: 9, height: 9, radius: 3 },
      labels: { colors: '#5F6C8E' },
    },

    tooltip: {
      shared: !distributed && shownSeries.length > 1,
      intersect: false,
      y: { formatter: (value) => shortCount(value) || '0' },
    },

    states: { active: { filter: { type: 'none' } } },

    noData: { text: '' },
  }), [shownCategories, shownSeries, barColors, stacked, distributed,
    legendVisible, customLegend, highlightPoint]);

  return (
    <div className={classes.chart_wrapper} ref={wrapperRef}>
      {customLegend && (
        <div className={classes.chart_legend}>
          {categories.map((category, index) => (
            <button
              type="button"
              key={category}
              className={`${classes.chart_legend_item} ${hidden.includes(category) ? classes.chart_legend_item_off : ''}`}
              onClick={() => toggleCategory(category)}
              onMouseEnter={() => highlightPoint(shownCategories.indexOf(category))}
              onMouseLeave={() => highlightPoint(null)}
            >
              <span
                className={classes.chart_legend_marker}
                style={{ background: colors[index % colors.length] }}
              />

              {category}
            </button>
          ))}
        </div>
      )}

      <Chart
        type="bar"
        height={height}
        series={shownSeries}
        options={options}
      />
    </div>
  );
};

PivotsChart.propTypes = {
  categories: PropTypes.array.isRequired,
  series: PropTypes.array.isRequired,
  height: PropTypes.number,
  stacked: PropTypes.bool,
  distributed: PropTypes.bool,
  showLegend: PropTypes.bool,
  colors: PropTypes.array,
};

export default PivotsChart;
